import React, { useState } from 'react';
import styles from './MegaMenu.module.css';
import Icons from '../../../../../../components/Icons/Icons';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../../../util/routes';
import {
  shopMenuSections,
  sellMenuOptions,
  travelMenuOptions,
} from '../../../../../../config/configExtras';
import useCommunityMenuOptions from '../../../../../../hooks/useCommunityMenuOptions';
import { RETREAT_LABEL } from '../../../../../../config/configRetreat';

// ✅ FAQ MENU
export const faqGuidesMenuOptions = [
  {
    id: 'buyers-guide',
    label: 'Buyer’s Guide',
    route: { name: 'BuyerGuidePage' },
  },
  {
    id: 'sellers-guide',
    label: 'Seller’s Guide',
    route: { name: 'SellerGuidePage' },
  },
  {
    id: 'faq',
    label: 'FAQ',
    route: { name: 'FAQPage' },
  },
];

// ✅ FINAL ORDER
const TOPS = ['Home', 'Shop', 'Travel', 'Sell', 'Community', 'Brands We Love', 'FAQ & Guides'];

// ✅ ONLY THESE HAVE DROPDOWN
const hasDropdownPanel = top =>
  ['Shop', 'Travel', 'Community', 'Sell', 'FAQ & Guides'].includes(top);

const shouldAnchorDropdownPanel = top =>
  ['Travel', 'Community', 'Sell', 'FAQ & Guides'].includes(top);

const MegaMenu = props => {
  const { viewport, onClose, isDrawer } = props;
  const isMobileLayout = viewport?.width && viewport?.width > 0 && viewport?.width < 768;

  const [activeCategory, setActiveCategory] = useState(null);
  const communityMenuOptions = useCommunityMenuOptions();

  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const shopSections = [
    ...shopMenuSections,
    { title: RETREAT_LABEL, items: travelMenuOptions },
  ];

  const navigateToSearch = searchParams => {
    const searchPath = createResourceLocatorString(
      'SearchPage',
      routeConfiguration,
      {},
      searchParams
    );
    history.push(searchPath);
    if (isDrawer && onClose) onClose();
  };

  const navigateToRoute = route => {
    if (!route?.name) return;

    const path = createResourceLocatorString(
      route.name,
      routeConfiguration,
      route.params || {},
      route.searchParams || {}
    );

    history.push(route.hash ? `${path}${route.hash}` : path);
    if (isDrawer && onClose) onClose();
  };

  const renderSectionPanel = (sections, onItemClick) => {
    return (
      <div className={styles.megaMenu}>
        {sections.map(section => (
          <div key={section.title} className={styles.brandContainer}>
            <div className={styles.groupTitle}>{section.title}</div>
            <div className={styles.modelsMenu}>
              {section.items.map(item => (
                <div
                  key={item.id}
                  className={styles.subLink}
                  role="button"
                  tabIndex={0}
                  onClick={() => onItemClick(item)}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onItemClick(item)}
                >
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderRoutePanel = items => {
    return (
      <div className={styles.simpleDropdown}>
        {items.map(item => (
          <button
            key={item.id}
            type="button"
            className={styles.dropdownActionButton}
            onClick={() => navigateToRoute(item.route)}
          >
            <span className={styles.dropdownLink}>{item.label}</span>
          </button>
        ))}
      </div>
    );
  };

  const toggleTopItem = item => {
    if (!hasDropdownPanel(item)) return;
    setActiveCategory(prev => (prev === item ? null : item));
  };

  const onTopClick = top => {
    // ✅ DIRECT LINKS
    if (top === 'Home') {
      navigateToRoute({ name: 'LandingPage' });
      return;
    }

    if (top === 'Brands We Love') {
      navigateToRoute({ name: 'OurAffiliatesPage' });
      return;
    }

    // ✅ DROPDOWN
    toggleTopItem(top);
  };

  const showPanel = cat => activeCategory === cat;

  return (
    <div className={styles.menu}>
      {TOPS.map(top => (
        <div
          key={top}
          className={
            shouldAnchorDropdownPanel(top)
              ? `${styles.menuItem} ${styles.menuItemAnchored}`
              : styles.menuItem
          }
          onMouseEnter={() => !isMobileLayout && hasDropdownPanel(top) && setActiveCategory(top)}
          onMouseLeave={() => !isMobileLayout && hasDropdownPanel(top) && setActiveCategory(null)}
        >
          <div className={styles.topLink}>
            <button
              type="button"
              className={styles.topLinkLabelButton}
              onClick={() => onTopClick(top)}
              aria-expanded={hasDropdownPanel(top) ? showPanel(top) : undefined}
              aria-haspopup={hasDropdownPanel(top) ? 'menu' : undefined}
            >
              {top}
            </button>

            {hasDropdownPanel(top) && (
              <button
                type="button"
                className={styles.topLinkToggle}
                aria-expanded={showPanel(top)}
                aria-haspopup="menu"
                aria-label={`Toggle ${top} menu`}
                onClick={() => toggleTopItem(top)}
              >
                <span className={showPanel(top) ? styles.topLinkIconOpen : styles.topLinkIcon}>
                  <Icons name="downAngle" />
                </span>
              </button>
            )}
          </div>

          {/* SHOP */}
          {top === 'Shop' &&
            showPanel('Shop') &&
            renderSectionPanel(shopSections, item =>
              item.route ? navigateToRoute(item.route) : navigateToSearch(item.searchParams)
            )}

          {top === 'Travel' && showPanel('Travel') && renderRoutePanel(travelMenuOptions)}

          {/* SELL */}
          {top === 'Sell' && showPanel('Sell') && renderRoutePanel(sellMenuOptions)}

          {/* COMMUNITY */}
          {top === 'Community' && showPanel('Community') && renderRoutePanel(communityMenuOptions)}

          {/* ✅ FAQ & GUIDES (FIXED) */}
          {top === 'FAQ & Guides' &&
            showPanel('FAQ & Guides') &&
            renderRoutePanel(faqGuidesMenuOptions)}
        </div>
      ))}
    </div>
  );
};

export default MegaMenu;
